import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Link, Prompt } from 'react-router-dom';
import moment from 'moment';
import CloseIcon from 'mdi-react/CloseIcon';
import ImagePlusIcon from 'mdi-react/ImagePlusIcon';
import HistoryIcon from 'mdi-react/HistoryIcon';
import FormModal from '../../../shared/components/custom/FormModal';
import Api from '../../../util/api';
import t, { partial } from '../../../util/translation/translation';
import EmbedPreview from './EmbedPreview';
import AiDraftFacts from './AiDraftFacts';
import { aiPageInfo } from './newsUtils';

const p = partial('NewsForm');
const s = partial('shared');
// How often to ask whether the page is done
const POLL_MS = 3000;
// As the content service allows (GenerateHtmlDraftDTO, EditHtmlDraftDTO)
const LENGTHS = {
  write: { min: 10, max: 8000 },
  edit: { min: 3, max: 2000 },
};

// Images the AI may see and use (as the content service allows)
const MAX_IMAGES = 6;

// An error key from the content service (aiDraft.*) as text
const errorText = (key) => t(`api.${key || 'aiDraft.failed'}`);

const shorten = (text, length = 90) =>
  text.length > length ? `${text.slice(0, length - 1)}…` : text;

// The images the AI gets: uploaded here, shown small, each removable
const DraftImages = ({ images, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const upload = async (event) => {
    const files = [...(event.target.files || [])].slice(
      0,
      MAX_IMAGES - images.length
    );
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setError(null);
    const urls = [];
    // One at a time, so the order they were picked in is kept
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('image', file);
        const { data } = await Api.images.uploadImage(formData);
        if (data?.url) urls.push(data.url);
      } catch (err) {
        setError(String(err));
      }
    }
    onChange([...images, ...urls]);
    setUploading(false);
  };
  return (
    <div className="ai-draft__images">
      <span className="form-header">{p('aiDraftImages')}</span>
      <div className="ai-draft__image-list">
        {images.map((url) => (
          <div className="ai-draft__image" key={url}>
            <img src={url} alt="" />
            <button
              type="button"
              className="ai-draft__image-remove"
              aria-label={p('aiDraftRemoveImage')}
              title={p('aiDraftRemoveImage')}
              onClick={() => onChange(images.filter((image) => image !== url))}
            >
              <CloseIcon size={14} />
            </button>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <label className="ai-draft__image-add">
            <ImagePlusIcon size={20} />
            <span>
              {uploading ? p('aiDraftUploading') : p('aiDraftAddImages')}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              disabled={uploading}
              onChange={upload}
            />
          </label>
        )}
      </div>
      <small className="ai-draft__usage">
        {p('aiDraftImagesHint', { max: MAX_IMAGES })}
      </small>
      {error && (
        <p className="ai-draft__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

// The club's latest pages and changes; a finished one opens like a new page
const RecentDrafts = ({ drafts, onOpen }) => {
  if (drafts === null) {
    return <p className="ai-draft__writing">{p('aiDraftRecentLoading')}</p>;
  }
  if (!drafts.length) {
    return <p className="ai-draft__writing">{p('aiDraftRecentEmpty')}</p>;
  }
  return (
    <ul className="ai-draft__recent">
      {drafts.map((item) => {
        const usable = ['succeeded', 'pending'].includes(item.status);
        return (
          <li key={item.id}>
            <button
              type="button"
              className="ai-draft__recent-item"
              disabled={!usable}
              onClick={() => onOpen(item)}
            >
              <span className="ai-draft__recent-title">
                {item.mode === 'html-edit' && (
                  <span className="ai-draft__badge">
                    {p('aiDraftRecentChange')}
                  </span>
                )}
                {shorten(item.title || item.brief)}
              </span>
              <span className="ai-draft__recent-meta">
                {moment(item.createdAt).format('DD.MM.YYYY HH:mm')}
                {item.status !== 'succeeded' &&
                  ` · ${p(`aiDraftStatus-${item.status}`)}`}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
/**
 * Write a page with AI: the editor describes it, Claude writes it as HTML
 * (a minute or two, asked for every few seconds), the editor checks the
 * preview and adds it to the article as a full-width iframe block.
 *
 * Given a page (`source`), it changes that page instead: the editor says
 * what to change and compares the page before and after.
 */
const AiDraftModal = ({
  isOpen,
  usage,
  source,
  onClose,
  onInsert,
  onStarted,
}) => {
  const editing = !!source;
  const { min, max } = LENGTHS[editing ? 'edit' : 'write'];
  const [brief, setBrief] = useState('');
  const [images, setImages] = useState([]);
  // Writing a new page: the club's recent pages instead of the brief, and
  // that list (null while it loads)
  const [showRecent, setShowRecent] = useState(false);
  const [recent, setRecent] = useState(null);
  // { id, status, title, html, error } once started; null while writing the brief
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  // Changing a page: the page as it was shown instead of the new one
  const [showBefore, setShowBefore] = useState(false);
  const timer = useRef(null);
  // The club's style for these pages is a setting (AiBrandPage)
  const canEditStyle = useSelector(
    (state) => !!state.admin?.me?.permissions?.includes('access-settings')
  );

  const stopPolling = () => {
    clearTimeout(timer.current);
    timer.current = null;
  };

  const follow = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const next = await Api.news.getAiDraft(id);
        setDraft(next);
        if (next.status === 'pending') {
          follow(id);
        } else {
          timer.current = null;
        }
      } catch (err) {
        timer.current = null;
        setError(String(err));
      }
    }, POLL_MS);
  };

  // Closed: stop asking (the page is still written, and counted). Opened
  // again while it's being written: ask again.
  useEffect(() => {
    if (!isOpen) {
      stopPolling();
    } else if (draft?.status === 'pending' && !timer.current) {
      follow(draft.id);
    }
    // follow and draft are read as they are when the modal opens or closes
    // eslint-disable-next-line
  }, [isOpen]);
  useEffect(() => stopPolling, []);
  // Another page to change: start over (a change being written goes on,
  // and is counted, but isn't followed)
  useEffect(() => {
    stopPolling();
    setDraft(null);
    setBrief('');
    setImages([]);
    setError(null);
    setShowBefore(false);
    setShowRecent(false);
  }, [source]);

  // Facts from a match, event, lottery or offer go under what's written, and
  // its image with the others while there's room
  const addFacts = (text, image) => {
    setBrief((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text));
    if (image) {
      setImages((prev) =>
        prev.includes(image) || prev.length >= MAX_IMAGES
          ? prev
          : [...prev, image]
      );
    }
  };

  const openRecent = async () => {
    setShowRecent(true);
    setRecent(null);
    try {
      setRecent(await Api.news.listAiDrafts());
    } catch (err) {
      setRecent([]);
      setError(String(err));
    }
  };

  // A page from the list: shown as if just written (followed when it's
  // still being written), with its brief there to write it again
  const openDraft = async (item) => {
    setError(null);
    try {
      const full = await Api.news.getAiDraft(item.id);
      if (item.mode === 'html') setBrief(item.brief);
      setShowRecent(false);
      setDraft(full);
      if (full.status === 'pending') follow(full.id);
    } catch (err) {
      setError(String(err));
    }
  };

  const start = async () => {
    setError(null);
    setStarting(true);
    try {
      const started = editing
        ? await Api.news.startAiEdit(
            source,
            brief.trim(),
            images,
            aiPageInfo(source)?.pageId
          )
        : await Api.news.startAiDraft(brief.trim(), images);
      setDraft(started);
      onStarted();
      follow(started.id);
    } catch (err) {
      setError(String(err));
    }
    setStarting(false);
  };

  const editBrief = () => {
    stopPolling();
    setDraft(null);
    setError(null);
    setShowBefore(false);
  };

  // While it's being written, leaving loses it: the page only gets into the
  // article from here. Warned on reload or closing the tab, and on leaving
  // for another page in the admin (the Prompt below)
  const writing = draft?.status === 'pending';
  useEffect(() => {
    if (!writing) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [writing]);

  const limitReached = !!usage && usage.used >= usage.limit;
  const briefLength = brief.trim().length;
  const status = draft?.status;

  return (
    <>
      <Prompt when={writing} message={p('aiDraftLeaveWarning')} />
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={editing ? p('aiEditTitle') : p('aiDraftTitle')}
        subtitle={editing ? p('aiEditHint') : p('aiDraftHint')}
      >
        <div className="qm-body ai-draft">
          {showRecent && !draft && (
            <RecentDrafts drafts={recent} onOpen={openDraft} />
          )}
          {!draft && !showRecent && (
            <>
              <label className="form-header" htmlFor="ai-draft-brief">
                {editing ? p('aiEditInstruction') : p('aiDraftBrief')}
              </label>
              <textarea
                id="ai-draft-brief"
                className="ai-draft__brief"
                rows={editing ? 4 : 8}
                maxLength={max}
                value={brief}
                placeholder={
                  editing
                    ? p('aiEditInstructionPlaceholder')
                    : p('aiDraftBriefPlaceholder')
                }
                onChange={(event) => setBrief(event.target.value)}
              />
              {!editing && <AiDraftFacts onAdd={addFacts} />}
              <DraftImages images={images} onChange={setImages} />
              {usage && (
                <p className="ai-draft__usage">
                  {p('aiDraftUsage', { used: usage.used, limit: usage.limit })}
                  {canEditStyle && (
                    <>
                      {' · '}
                      <Link to="/ai_brand" onClick={onClose}>
                        {p('aiDraftStyleLink')}
                      </Link>
                    </>
                  )}
                  {!editing && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="ai-draft__link"
                        onClick={openRecent}
                      >
                        <HistoryIcon size={14} /> {p('aiDraftRecent')}
                      </button>
                    </>
                  )}
                </p>
              )}
            </>
          )}
          {status === 'pending' && (
            <p className="ai-draft__writing" role="status">
              {editing ? p('aiEditWriting') : p('aiDraftWriting')}
            </p>
          )}
          {status === 'succeeded' && (
            <>
              {!editing && draft.title && (
                <h4 className="ai-draft__title">{draft.title}</h4>
              )}
              {editing && (
                <div className="ai-draft__compare" role="group">
                  <Button
                    type="button"
                    size="sm"
                    outline={showBefore}
                    color="primary"
                    className="mb-0"
                    aria-pressed={!showBefore}
                    onClick={() => setShowBefore(false)}
                  >
                    {p('aiEditAfter')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    outline={!showBefore}
                    color="primary"
                    className="mb-0"
                    aria-pressed={showBefore}
                    onClick={() => setShowBefore(true)}
                  >
                    {p('aiEditBefore')}
                  </Button>
                </div>
              )}
              <div className="ai-draft__preview">
                <EmbedPreview
                  html={editing && showBefore ? source : draft.html}
                  fullWidth
                />
              </div>
              <p className="ai-draft__usage">{p('aiDraftCheck')}</p>
            </>
          )}
          {draft && !['pending', 'succeeded'].includes(status) && (
            <p className="ai-draft__error" role="alert">
              {errorText(draft.error)}
            </p>
          )}
          {error && (
            <p className="ai-draft__error" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="qm-footer">
          <Button
            type="button"
            outline
            color="secondary"
            className="mb-0"
            onClick={
              showRecent && !draft
                ? () => setShowRecent(false)
                : draft && status !== 'pending'
                  ? editBrief
                  : onClose
            }
          >
            {showRecent && !draft
              ? p('aiDraftRecentBack')
              : draft && status !== 'pending'
                ? p(editing ? 'aiEditChangeInstruction' : 'aiDraftEditBrief')
                : s('cancel')}
          </Button>
          {!draft && !showRecent && (
            <Button
              type="button"
              color="primary"
              className="mb-0"
              disabled={
                starting ||
                limitReached ||
                briefLength < min ||
                briefLength > max
              }
              onClick={start}
            >
              {editing ? p('aiEditStart') : p('aiDraftStart')}
            </Button>
          )}
          {status === 'succeeded' && (
            <Button
              type="button"
              color="primary"
              className="mb-0"
              onClick={() => {
                onInsert(draft.html);
                setDraft(null);
                setBrief('');
                setImages([]);
              }}
            >
              {editing ? p('aiEditUse') : p('aiDraftInsert')}
            </Button>
          )}
        </div>
      </FormModal>
    </>
  );
};

export default AiDraftModal;
